import { FormInstance } from 'antd';
import moment from 'moment';
import React, { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ProjectFinancialsContractType } from 'api/project';
import { Form } from 'components/atoms/form';
import { InputNumber } from 'components/atoms/input';
import { Select } from 'components/atoms/select';
import { DescriptiveOption } from 'components/atoms/select/custom-components';
import { projectFinancialsContractTypeToIconMap } from 'components/organisms/projects/project-financials/utils/common';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import { useAccountProfile } from 'queries/account';
import { useRateCardsQuery } from 'queries/rate-cards';
import { Col, Row } from './styled';

const EffectiveDateWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
`;
const EffectiveFrom = styled.span`
  color: var(--gray-50);
`;

const BillingMethodWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

const BillingDescriptionWrapper = styled.div`
  margin-left: var(--spacing-06);
`;

export interface FinancialInfoStepFormValues {
  rateCardId: {
    label: string;
    value: number;
  };
  financialContractType: {
    label: string;
    value: ProjectFinancialsContractType;
  };
  projectBudget: number;
  projectHours: number;
}

interface FinancialInfoStep {
  form: FormInstance<FinancialInfoStepFormValues>;
  onValuesChange: (values: any, allValues: unknown) => void;
}

export const FinancialInfoStep: FC<FinancialInfoStep> = ({
  form,
  onValuesChange,
}) => {
  const translate = useTranslate();
  const { data: accountData } = useAccountProfile();
  const { data: rateCardsList } = useRateCardsQuery(true);

  const [billingMethod, setBillingMethod] = useState<
    ProjectFinancialsContractType | undefined
  >(() => form.getFieldValue('financialContractType')?.value);

  const billingMethodOptions = useMemo(
    () => [
      ...Object.values(ProjectFinancialsContractType)
        .filter((type) => type !== ProjectFinancialsContractType.SUBSCRIPTION)
        .map((billingType) => ({
          label: (
            <BillingMethodWrapper>
              {projectFinancialsContractTypeToIconMap[billingType]}
              {translate(`project.financials.type.${billingType}.title`)}
            </BillingMethodWrapper>
          ),
          description: (
            <BillingDescriptionWrapper>
              {translate(`project.financials.type.${billingType}.description`)}
            </BillingDescriptionWrapper>
          ),
          value: billingType,
        })),
    ],
    [translate],
  );

  const rateCardsOptions = useMemo(
    () =>
      (rateCardsList ?? [])
        .filter((rateCard) => rateCard.enabled)
        .map((rateCard) => ({
          label: rateCard.rateCardName,
          value: rateCard.rateCardId,
          description: (
            <EffectiveDateWrapper>
              <EffectiveFrom>
                {translate('projects.create.rate_cards.effective_from')}
              </EffectiveFrom>
              {moment(rateCard.activeVersion?.effectiveDate).format(
                'DD MMM YY',
              )}
            </EffectiveDateWrapper>
          ),
        })),
    [rateCardsList, translate],
  );

  const isHidden =
    !billingMethod ||
    billingMethod === ProjectFinancialsContractType.NONBILLABLE;

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        onValuesChange={onValuesChange}
        name="financialsInfoStepForm"
      >
        <Row>
          <Col span={12}>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: <FormattedMessage id="input.required" />,
                },
              ]}
              name="financialContractType"
              label="Billing method"
            >
              <Select
                options={billingMethodOptions}
                isClearable
                value={{
                  label: translate(
                    `project.financials.type.${billingMethod}.title` as TranslationKey,
                  ),
                  value: billingMethod,
                }}
                onChange={(selected) => {
                  setBillingMethod(selected?.value);
                }}
                components={{ Option: DescriptiveOption }}
                menuPlacement="auto"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 'var(--zindex-popover-low)',
                  }),
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            {billingMethod === ProjectFinancialsContractType.NONBILLABLE ? (
              <Form.Item
                shouldUpdate
                name="projectHours"
                label={translate('project.financials.project_hours')}
              >
                <InputNumber />
              </Form.Item>
            ) : (
              <Form.Item hidden={isHidden} name="rateCardId" label="Rate card">
                <Select
                  options={rateCardsOptions}
                  isClearable
                  menuPlacement="auto"
                  components={{ Option: DescriptiveOption }}
                  noOptionsMessage={() => (
                    <FormattedMessage id="projects.create.financials.no_rate_card" />
                  )}
                  styles={{
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 'var(--zindex-popover-low)',
                    }),
                  }}
                />
              </Form.Item>
            )}
          </Col>
        </Row>

        <Row>
          <Col span={12}>
            <Form.Item
              name="projectBudget"
              shouldUpdate
              hidden={isHidden}
              rules={[
                {
                  required:
                    billingMethod === ProjectFinancialsContractType.FIXED,
                  message: <FormattedMessage id="input.required" />,
                },
              ]}
              label={
                billingMethod === ProjectFinancialsContractType.TM
                  ? translate(
                      'project.settings.budget.rate.TIME_AND_MATERIAL.title',
                    )
                  : translate('project.settings.budget.rate.FIXED_FEE.title')
              }
            >
              <InputNumber
                min={0}
                formatter={(value) =>
                  `${
                    accountData?.currency?.currencySymbol ?? '$'
                  } ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              shouldUpdate
              hidden={isHidden}
              name="projectHours"
              label={translate('project.financials.project_hours')}
            >
              <InputNumber min={0} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </>
  );
};
